/**
 * Makes colour changes on the PDP feel instant: every photo changes together, like Comfrt.
 *
 * A colour change normally waits for the server (section fetch, ~300ms), then the new gallery's photos start
 * downloading and pop in one by one. This script does the waiting ahead of time:
 *
 *  1. For a colour it prefetches (a) the section response the click would request (variant-picker.js keeps it
 *     in a short-lived cache and reuses it on click) and (b) the colour's first photos, downloaded with the
 *     gallery's own srcset + sizes so the browser caches the very files the new gallery will ask for.
 *  2. That happens on hover / focus / touch of a swatch, and, on a fast connection, for every colour shortly
 *     after the page has finished loading.
 *
 * The swap itself stays server-rendered: variant-picker.js applies the (cached) response and media-gallery.js
 * replaces the whole gallery in one step once its first photos are decoded. Nothing here touches the DOM, and
 * no event is stopped or cancelled, so a failure only means the click behaves as it always did.
 *
 * Data comes from snippets/pdp-colour-media-data.liquid.
 */
const COLOUR_OPTION = /colou?r|curated packs/i;
// Must equal `gallery_widths` in snippets/product-media-gallery-content.liquid (and pdp-hero-preload.liquid).
const WIDTHS = [240, 352, 832, 1200, 1600];
// Photos prefetched per colour (the 4 shown above the fold, now that the video no longer sits second), and how
// many colours are warmed in the background.
const PHOTOS_PER_COLOUR = 4;
const MAX_IDLE_COLOURS = 12;

const photosDone = new Set();
const colourDone = new Set();

if (!window.__seneColourSwapFast) {
  window.__seneColourSwapFast = true;

  /** @returns {Record<string, string[]> | null} */
  const colourMedia = () => {
    try {
      const raw = document.querySelector('script[data-colour-media]')?.textContent;
      return raw ? JSON.parse(raw).colours : null;
    } catch (e) {
      return null;
    }
  };

  /** The PDP's main variant picker (not a product card's). */
  const mainPicker = () => document.querySelector('variant-picker[data-template-product-match="true"]');

  /** Same srcset image_tag builds for the gallery: one entry per width, `width` swapped in the URL. */
  const srcsetFor = (url) => WIDTHS.map((w) => `${url.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');

  const gallerySizes = () => {
    const img = document.querySelector('media-gallery img.product-media__image[sizes]');
    return img ? img.getAttribute('sizes') : null;
  };

  const radioFor = (colour) => {
    const picker = mainPicker();
    if (!picker) return null;
    for (const input of picker.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement && input.value === colour && COLOUR_OPTION.test(input.dataset.optionType || ''))
        return input;
    }
    return null;
  };

  /** @param {EventTarget | null} target */
  const colourFromTarget = (target) => {
    if (!(target instanceof Element)) return null;

    // Variation E buy box swatches (rows 1-3).
    const sw = target.closest('[data-sw]');
    if (sw instanceof HTMLElement && sw.dataset.sw) return sw.dataset.sw;

    // Standard variant picker swatches / pills.
    const input = target.matches('input[type="radio"]')
      ? target
      : target.closest('label')?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return null;
    if (!COLOUR_OPTION.test(input.dataset.optionType || '')) return null;
    if (!input.closest('variant-picker') || input.closest('product-card, quick-add-dialog')) return null;
    return input.value;
  };

  const prefetchPhotos = (colour) => {
    const urls = colourMedia()?.[colour];
    const sizes = gallerySizes();
    if (!urls || !sizes) return;

    for (const url of urls.slice(0, PHOTOS_PER_COLOUR)) {
      if (photosDone.has(url)) continue;
      photosDone.add(url);
      const img = new Image();
      img.fetchPriority = 'low';
      img.sizes = sizes;
      img.srcset = srcsetFor(url);
      img.src = url.replace(/width=\d+/, 'width=832');
    }
  };

  /** Warms everything a click on `colour` will need. Returns the section promise (or null). */
  const prefetchColour = (colour) => {
    prefetchPhotos(colour);

    const radio = radioFor(colour);
    const picker = mainPicker();
    if (!radio || radio.checked || !picker || typeof picker.prefetchOption !== 'function') return null;
    return picker.prefetchOption(radio);
  };

  const onIntent = (event) => {
    try {
      const colour = colourFromTarget(event.target);
      if (colour) prefetchColour(colour);
    } catch (e) {
      /* never interfere with the page */
    }
  };

  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });

  /** Warm every other colour in the background, one at a time, on a fast connection only. */
  const warmAllColours = async () => {
    try {
      const connection = /** @type {any} */ (navigator).connection;
      if (connection?.saveData) return;
      if (connection?.effectiveType && connection.effectiveType !== '4g') return;

      // The colours actually offered by the picker (the data can list more), other than the one showing.
      const picker = mainPicker();
      if (!picker) return;
      const offered = [...picker.querySelectorAll('input[type="radio"]')]
        .filter((input) => input instanceof HTMLInputElement && COLOUR_OPTION.test(input.dataset.optionType || '') && !input.checked)
        .map((input) => /** @type {HTMLInputElement} */ (input).value);
      const colours = [...new Set(offered)].slice(0, MAX_IDLE_COLOURS);
      for (const colour of colours) {
        if (colourDone.has(colour)) continue;
        colourDone.add(colour);
        const pending = prefetchColour(colour);
        if (pending) await pending.catch(() => {});
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    } catch (e) {
      /* never interfere with the page */
    }
  };

  const scheduleWarmUp = () => {
    const start = () => (window.requestIdleCallback ? window.requestIdleCallback(warmAllColours, { timeout: 4000 }) : warmAllColours());
    setTimeout(start, 2000);
  };

  if (document.readyState === 'complete') scheduleWarmUp();
  else window.addEventListener('load', scheduleWarmUp, { once: true });
}
